import { Directive, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgModel } from '@angular/forms';
import { TimeoutService } from '@df/ng/timeout.service';
import { WINDOW } from '@df/dom/globals';
import { inject } from '@angular/core';

/** EXAMPLE OF USING
 *  <input class="input"
 *         type="text"
 *         name="date"
 *         [(ngModel)]="data.date"
 *         inputMask
 *         mask="99/99/99"
 *         placeholder="99/99/99"
 *         slotChar="dd/mm/yy"
 *         required>
 */

@Directive({
    selector: 'input[inputMask]',
    standalone: true,
    host: {
        '[class.input-mask-filled]': 'filled',
        '[class.input-mask-focus]': 'focused',
        '(focus)': 'onInputFocus($event)',
        '(blur)': 'onInputBlur($event)',
        '(keydown)': 'onKeyDown($event)',
        '(keypress)': 'onKeyPress($event)',
        '(input)': 'onInputChange($event)',
        '(paste)': 'handleInputChange($event)'
    }
})
export class InputMaskDirective implements OnInit {
    protected tests: any[] = [];

    protected partialPosition: any;

    protected firstNonMaskPos?: number;

    protected _mask?: string;

    protected len: number = 0;

    protected oldVal?: string;

    protected defs: any;

    protected lastRequiredNonMaskPos: any;

    protected buffer: any;

    protected defaultBuffer?: string;

    protected value: any;

    protected focusText?: string;

    protected filled?: boolean;

    protected focused?: boolean;

    protected androidChrome?: boolean;

    protected caretTimeoutId: any;

    @Input() slotChar: string = '_';

    @Input() characterPattern: string = '[A-Za-z]';

    @Input() autoClear: boolean = true;

    @Input() unmask?: boolean;

    @Input() readonly?: boolean;

    @Input() disabled?: boolean;

    @Input() get mask(): string | undefined {
        return this._mask;
    }

    set mask(val: string) {
        this._mask = val;

        this.initMask();
        this.checkVal();
        this.focusText = this.elementRef.nativeElement.value;
        this.updateFilledState();
        this.onModelChange(this.value);
    }

    @Output() completeChange: EventEmitter<any> = new EventEmitter();

    @Output() focusChange: EventEmitter<any> = new EventEmitter();

    @Output() blurChange: EventEmitter<any> = new EventEmitter();

    @Output() inputChange: EventEmitter<any> = new EventEmitter();

    private window = inject(WINDOW);

    constructor(
        protected ngModel: NgModel,
        protected elementRef: ElementRef<HTMLInputElement>,
        private timeoutService: TimeoutService
    ) {}

    ngOnInit() {
        const ua = this.window.navigator?.userAgent;
        this.androidChrome = ua && /chrome/i.test(ua) && /android/i.test(ua) ? true : false;

        this.initMask();
    }

    initMask() {
        this.tests = [];
        this.len = this.partialPosition = this.mask?.length || 0;
        this.firstNonMaskPos = undefined;
        this.defs = {
            9: '[0-9]',
            a: this.characterPattern,
            '*': `${this.characterPattern}|[0-9]`
        };

        const maskTokens = this.mask?.split('') || [];
        for (let i = 0; i < maskTokens.length; i++) {
            const c = maskTokens[i];
            if (c === '?') {
                this.len--;
                this.partialPosition = i;
            } else if (this.defs[c]) {
                this.tests.push(new RegExp(this.defs[c]));
                if (this.firstNonMaskPos === null) {
                    this.firstNonMaskPos = this.tests.length - 1;
                }
                if (i < this.partialPosition) {
                    this.lastRequiredNonMaskPos = this.tests.length - 1;
                }
            } else {
                this.tests.push(null);
            }
        }

        this.buffer = [];
        for (let i = 0; i < maskTokens.length; i++) {
            const c = maskTokens[i];
            if (c !== '?') {
                if (this.defs[c]) this.buffer.push(this.getPlaceholder(i));
                else this.buffer.push(c);
            }
        }
        this.defaultBuffer = this.buffer.join('');
    }

    caret(first?: number, last?: number) {
        let range;
        let begin;
        let end;

        if (!this.elementRef.nativeElement.offsetParent || this.elementRef.nativeElement !== document.activeElement) {
            return;
        }

        if (typeof first === 'number') {
            begin = first;
            end = typeof last === 'number' ? last : begin;
            if (this.elementRef.nativeElement.setSelectionRange) {
                this.elementRef.nativeElement.setSelectionRange(begin, end);
            } else if (this.elementRef.nativeElement['createTextRange']) {
                range = this.elementRef.nativeElement['createTextRange']();
                range.collapse(true);
                range.moveEnd('character', end);
                range.moveStart('character', begin);
                range.select();
            }
        } else {
            if ('setSelectionRange' in this.elementRef.nativeElement) {
                begin = this.elementRef.nativeElement.selectionStart;
                end = this.elementRef.nativeElement.selectionEnd;
            } else if (document['selection'] && document['selection'].createRange) {
                range = document['selection'].createRange();
                begin = 0 - range.duplicate().moveStart('character', -100000);
                end = begin + range.text.length;
            }

            return { begin, end };
        }
    }

    isCompleted(): boolean {
        if (typeof this.firstNonMaskPos !== 'undefined') {
            for (let i = this.firstNonMaskPos; i <= this.lastRequiredNonMaskPos; i++) {
                if (this.tests[i] && this.buffer[i] === this.getPlaceholder(i)) {
                    return false;
                }
            }
        }
        return true;
    }

    getPlaceholder(i: number) {
        if (i < this.slotChar.length) {
            return this.slotChar.charAt(i);
        }
        return this.slotChar.charAt(0);
    }

    seekNext(pos) {
        while (++pos < this.len && !this.tests[pos]);
        return pos;
    }

    seekPrev(pos) {
        while (--pos >= 0 && !this.tests[pos]);
        return pos;
    }

    shiftL(begin: number, end: number) {
        let i;
        let j;

        if (begin < 0) {
            return;
        }

        for (i = begin, j = this.seekNext(end); i < this.len; i++) {
            if (this.tests[i]) {
                if (j < this.len && this.tests[i].test(this.buffer[j])) {
                    this.buffer[i] = this.buffer[j];
                    this.buffer[j] = this.getPlaceholder(j);
                } else {
                    break;
                }

                j = this.seekNext(j);
            }
        }
        this.writeBuffer();
        this.caret(Math.max(this.firstNonMaskPos!, begin));
    }

    shiftR(pos) {
        let i;
        let c;
        let j;
        let t;

        for (i = pos, c = this.getPlaceholder(pos); i < this.len; i++) {
            if (this.tests[i]) {
                j = this.seekNext(i);
                t = this.buffer[i];
                this.buffer[i] = c;
                if (j < this.len && this.tests[j].test(t)) {
                    c = t;
                } else {
                    break;
                }
            }
        }
    }

    handleAndroidInput(e) {
        const curVal = this.elementRef.nativeElement.value;
        const pos = this.caret();
        if (this.oldVal && this.oldVal.length && this.oldVal.length > curVal.length) {
            // a deletion or backspace happened
            this.checkVal(true);
            if (pos) {
                while (pos.begin > 0 && !this.tests[pos.begin - 1]) pos.begin--;
                if (pos.begin === 0) {
                    while (pos.begin < this.firstNonMaskPos! && !this.tests[pos.begin]) pos.begin++;
                }
            }

            this.timeoutService.setTimeout(() => {
                if (pos) {
                    this.caret(pos.begin, pos.begin);
                }
                this.updateModel(e);
                if (this.isCompleted()) {
                    this.completeChange.emit();
                }
            }, 0);
        } else {
            this.checkVal(true);
            if (pos) {
                while (pos.begin < this.len && !this.tests[pos.begin]) pos.begin++;
            }
            this.timeoutService.setTimeout(() => {
                if (pos) {
                    this.caret(pos.begin, pos.begin);
                }
                this.updateModel(e);
                if (this.isCompleted()) {
                    this.completeChange.emit();
                }
            }, 0);
        }
    }

    onInputBlur(e) {
        this.focused = false;
        this.checkVal();
        this.updateFilledState();
        this.blurChange.emit(e);

        if (this.elementRef.nativeElement.value !== this.focusText || this.elementRef.nativeElement.value !== this.value) {
            this.updateModel(e);
            const event = document.createEvent('HTMLEvents');
            event.initEvent('change', true, false);
            this.elementRef.nativeElement.dispatchEvent(event);
        }
    }

    onKeyDown(e) {
        if (this.readonly) {
            return;
        }

        const k = e.which || e.keyCode;
        let pos;
        let begin;
        let end;
        const iPhone = /iphone/i.test(this.window.navigator?.userAgent);
        this.oldVal = this.elementRef.nativeElement.value;

        // backspace, delete, and escape get special treatment
        if (k === 8 || k === 46 || (iPhone && k === 127)) {
            pos = this.caret();
            begin = pos.begin;
            end = pos.end;

            if (end - begin === 0) {
                begin = k !== 46 ? this.seekPrev(begin) : (end = this.seekNext(begin - 1));
                end = k === 46 ? this.seekNext(end) : end;
            }

            this.clearBuffer(begin, end);
            this.shiftL(begin, end - 1);
            this.updateModel(e);
            this.inputChange.emit(e);

            e.preventDefault();
        } else if (k === 13) {
            // enter
            this.onInputBlur(e);
            this.updateModel(e);
        } else if (k === 27) {
            // escape
            this.elementRef.nativeElement.value = this.focusText || '';
            this.caret(0, this.checkVal());
            this.updateModel(e);

            e.preventDefault();
        }
    }

    onKeyPress(e) {
        if (this.readonly) {
            return;
        }

        const k = e.which || e.keyCode;
        const pos = this.caret();
        let p;
        let c;
        let next;
        let completed;

        if (e.ctrlKey || e.altKey || e.metaKey || k < 32 || (k > 34 && k < 41)) {
            // Ignore
            return;
        } else if (k && k !== 13 && pos) {
            if (pos.end - pos.begin !== 0) {
                this.clearBuffer(pos.begin, pos.end);
                this.shiftL(pos.begin, pos.end - 1);
            }

            p = this.seekNext(pos.begin - 1);
            if (p < this.len) {
                c = String.fromCharCode(k);
                if (this.tests[p].test(c)) {
                    this.shiftR(p);

                    this.buffer[p] = c;
                    this.writeBuffer();
                    next = this.seekNext(p);

                    if (/android/i.test(this.window.navigator?.userAgent)) {
                        // Path for CSP Violation on FireFox OS 1.1
                        const proxy = () => {
                            this.caret(next);
                        };

                        this.timeoutService.setTimeout(proxy, 0);
                    } else {
                        this.caret(next);
                    }

                    if (pos.begin <= this.lastRequiredNonMaskPos) {
                        completed = this.isCompleted();
                    }

                    this.inputChange.emit(e);
                }
            }
            e.preventDefault();
        }

        this.updateModel(e);

        this.updateFilledState();

        if (completed) {
            this.completeChange.emit();
        }
    }

    clearBuffer(start, end) {
        let i;
        for (i = start; i < end && i < this.len!; i++) {
            if (this.tests[i]) {
                this.buffer[i] = this.getPlaceholder(i);
            }
        }
    }

    writeBuffer() {
        this.elementRef.nativeElement.value = this.buffer.join('');
    }

    checkVal(allow?: boolean) {
        // try to place characters where they belong
        const test = this.elementRef.nativeElement.value;
        let lastMatch = -1;
        let i;
        let c;
        let pos;

        for (i = 0, pos = 0; i < this.len!; i++) {
            if (this.tests[i]) {
                this.buffer[i] = this.getPlaceholder(i);
                while (pos++ < test.length) {
                    c = test.charAt(pos - 1);
                    if (this.tests[i].test(c)) {
                        this.buffer[i] = c;
                        lastMatch = i;
                        break;
                    }
                }
                if (pos > test.length) {
                    this.clearBuffer(i + 1, this.len);
                    break;
                }
            } else {
                if (this.buffer[i] === test.charAt(pos)) {
                    pos++;
                }
                if (i < this.partialPosition) {
                    lastMatch = i;
                }
            }
        }
        if (allow) {
            this.writeBuffer();
        } else if (lastMatch + 1 < this.partialPosition) {
            if (this.autoClear || this.buffer.join('') === this.defaultBuffer) {
                // Invalid value. Remove it and replace it with the
                // mask, which is the default behavior.
                if (this.elementRef.nativeElement.value) this.elementRef.nativeElement.value = '';
                this.clearBuffer(0, this.len);
            } else {
                // Invalid value, but we opt to show the value to the
                // user and allow them to correct their mistake.
                this.writeBuffer();
            }
        } else {
            this.writeBuffer();
            this.elementRef.nativeElement.value = this.elementRef.nativeElement.value.substring(0, lastMatch + 1);
        }
        return this.partialPosition ? i : this.firstNonMaskPos;
    }

    onInputFocus(event) {
        if (this.readonly) {
            return;
        }

        this.focused = true;

        clearTimeout(this.caretTimeoutId);
        let pos;

        this.focusText = this.elementRef.nativeElement.value;

        pos = this.checkVal();

        this.caretTimeoutId = this.timeoutService.setTimeout(() => {
            if (this.elementRef.nativeElement !== document.activeElement) {
                return;
            }
            this.writeBuffer();
            if (pos === this.mask?.replace('?', '').length) {
                this.caret(0, pos);
            } else {
                this.caret(pos);
            }
        }, 10);

        this.focusChange.emit(event);
    }

    onInputChange(event) {
        if (this.androidChrome) this.handleAndroidInput(event);
        else this.handleInputChange(event);

        this.inputChange.emit(event);
    }

    handleInputChange(event) {
        if (this.readonly) {
            return;
        }

        this.timeoutService.setTimeout(() => {
            const pos = this.checkVal(true);
            this.caret(pos);
            this.updateModel(event);
            if (this.isCompleted()) {
                this.completeChange.emit();
            }
        }, 0);
    }

    getUnmaskedValue() {
        const unmaskedBuffer: string[] = [];
        for (let i = 0; i < this.buffer.length; i++) {
            const c = this.buffer[i];
            if (this.tests[i] && c !== this.getPlaceholder(i)) {
                unmaskedBuffer.push(c);
            }
        }

        return unmaskedBuffer.join('');
    }

    updateModel(e) {
        const updatedValue = this.unmask ? this.getUnmaskedValue() : e.target.value;
        if (updatedValue !== null || updatedValue !== undefined) {
            this.value = updatedValue;
            this.onModelChange(this.value);
        }
    }

    updateFilledState() {
        this.filled = this.elementRef.nativeElement && this.elementRef.nativeElement.value !== '';
    }

    focus() {
        this.elementRef.nativeElement.focus();
    }

    onModelChange(value: string) {
        this.ngModel.update.emit(value);
    }
}
